import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import {
  Trophy,
  ChevronRight,
  Save,
  Send,
  Trash2,
  Loader2,
  Clock,
  CheckCircle2,
  Lock,
  Plus,
  AlertCircle,
  ArrowLeft,
  User,
  Calendar,
  Info,
  RefreshCw,
  Calculator,
  Database,
  Pencil,
  X,
  Check,
  EyeOff,
  Eye,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  TrendingUp,
  FileText,
  Download,
  FileSpreadsheet,
  ExternalLink,
  Mail,
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

// ─────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function formatDateTime(d) {
  if (!d) return null;
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}
function formatCurrency(v) {
  if (v === null || v === undefined) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v);
}
function formatPct(v) {
  if (v === null || v === undefined) return '—';
  return `${parseFloat(v).toFixed(1)}%`;
}

const _SYSTEM_KEYS = new Set(['revenue', 'on_time']);

// ─────────────────────────────────────────────────────
// Small UI components
// ─────────────────────────────────────────────────────

function StatusBadge({ status }) {
  if (status === 'published') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1.5">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Published
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-amber-700 border-amber-300 bg-amber-50 gap-1.5">
      <Clock className="h-3.5 w-3.5" />
      Draft
    </Badge>
  );
}

function AuditField({ label, value, icon: Icon }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <span className="text-slate-500">{label}: </span>
        <span className="text-slate-700">{value}</span>
      </div>
    </div>
  );
}

function SourceBadge({ source }) {
  if (!source || source === 'system_derived') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
        <Database className="h-2.5 w-2.5" />
        System
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
      <Pencil className="h-2.5 w-2.5" />
      Override
    </span>
  );
}

function EligibilityBadge({ isEligible, isCalculated, isExcluded }) {
  if (isExcluded) {
    return <Badge variant="outline" className="text-slate-500 border-slate-300">Excluded</Badge>;
  }
  if (!isCalculated) {
    return <Badge variant="outline" className="text-slate-400 border-slate-200 text-xs">Pending</Badge>;
  }
  if (isEligible) {
    return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Eligible</Badge>;
  }
  return <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50">Ineligible</Badge>;
}

// ─────────────────────────────────────────────────────
// Inline editable number cell
// ─────────────────────────────────────────────────────

function EditableCell({ value, onSave, readOnly, prefix, suffix, placeholder }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState('');
  const inputRef = useRef(null);

  const startEdit = () => {
    if (readOnly) return;
    setLocal(value !== null && value !== undefined ? String(value) : '');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const commit = () => {
    setEditing(false);
    const parsed = parseFloat(local);
    if (!isNaN(parsed)) {
      onSave(parsed);
    }
  };

  const cancel = () => setEditing(false);

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        {prefix && <span className="text-slate-400 text-xs">{prefix}</span>}
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          className="w-20 text-xs border border-blue-400 rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }}
          placeholder={placeholder}
        />
        {suffix && <span className="text-slate-400 text-xs">{suffix}</span>}
        <button onMouseDown={(e) => { e.preventDefault(); commit(); }} className="text-emerald-600 hover:text-emerald-700"
          data-testid="editable-cell-commit-btn">
          <Check className="h-3 w-3" />
        </button>
        <button onMouseDown={(e) => { e.preventDefault(); cancel(); }} className="text-slate-400 hover:text-slate-600"
          data-testid="editable-cell-cancel-btn">
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  const displayVal = value !== null && value !== undefined
    ? (prefix === '£' ? formatCurrency(value) : `${parseFloat(value).toFixed(prefix === '' ? 1 : 2)}${suffix || ''}`)
    : <span className="text-slate-300 italic text-xs">{placeholder || 'not set'}</span>;

  return (
    <button
      onClick={startEdit}
      disabled={readOnly}
      className={`flex items-center gap-1 text-sm text-left w-full group ${readOnly ? 'cursor-default' : 'hover:text-blue-600 cursor-pointer'}`}
    >
      <span>{displayVal}</span>
      {!readOnly && (
        <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-50 transition-opacity text-slate-400 flex-shrink-0" />
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────
// Categorical input cell (dropdown for categorical criteria)
// ─────────────────────────────────────────────────────

function CategoricalCell({ criterion, value, onSave, readOnly }) {
  const categories = criterion.categories || [];
  const currentLabel = categories.find(c => c.value === value)?.label || value || '— select —';

  if (readOnly) {
    return <span className="text-sm text-slate-700">{currentLabel}</span>;
  }

  return (
    <Select
      value={value ?? ''}
      onValueChange={onSave}
    >
      <SelectTrigger className="h-7 text-xs w-full" data-testid={`categorical-${criterion.key}`}>
        <SelectValue placeholder="— select —" />
      </SelectTrigger>
      <SelectContent>
        {categories.map(cat => (
          <SelectItem key={cat.value} value={cat.value} className="text-xs">
            <span className="flex items-center gap-2">
              {cat.label}
              <span className="text-slate-400">{cat.score_pct}%</span>
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}



const BeneficiaryRow = React.memo(function BeneficiaryRow({
  ben,
  scheme,
  readOnly,
  isAdmin,
  hasPartTime,
  isLeadershipRun,
  onUpdate,
  onExcludeToggle,
  onDelete,
}) {
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  const manualCriteria = scheme
    ? (scheme.criteria || []).filter(c => {
        if (isLeadershipRun) return c.component === 'quarterly';
        return !_SYSTEM_KEYS.has(c.key);
      })
    : [];

  const overrides = scheme ? (scheme.overrides || []) : [];

  const handleRevenueChange = async (val) => {
    setSaving(true);
    try {
      await onUpdate(ben.id, {
        revenue_amount: val,
        revenue_source: 'manual_override',
        criteria_inputs: { ...ben.criteria_inputs, revenue: val },
      });
    } finally { setSaving(false); }
  };

  const handleOnTimeChange = async (val) => {
    const clamped = Math.min(100, Math.max(0, val));
    setSaving(true);
    try {
      await onUpdate(ben.id, {
        on_time_pct: clamped,
        on_time_source: 'manual_override',
        criteria_inputs: { ...ben.criteria_inputs, on_time: clamped },
      });
    } finally { setSaving(false); }
  };

  const handleManualCriterionChange = async (key, val) => {
    // For categorical criteria, val is a string (e.g., 'positive') — do not clamp.
    // For factor criteria, val is already converted from % to decimal — store as-is.
    // For numeric criteria, clamp to 0-100.
    let valueToSave;
    if (typeof val === 'string') {
      valueToSave = val;
    } else {
      // Check if this is a factor criterion
      const criterion = (scheme?.criteria || []).find(c => c.key === key);
      if (criterion?.scoring_type === 'factor') {
        valueToSave = val; // Already decimal, passed through from EditableCell
      } else {
        valueToSave = Math.min(100, Math.max(0, val));
      }
    }
    setSaving(true);
    try {
      await onUpdate(ben.id, {
        criteria_inputs: { ...ben.criteria_inputs, [key]: valueToSave },
      });
    } finally { setSaving(false); }
  };

  const handleOverrideFlagChange = async (key, checked) => {
    setSaving(true);
    try {
      await onUpdate(ben.id, {
        override_flags: { ...ben.override_flags, [key]: checked },
      });
    } finally { setSaving(false); }
  };

  const handlePtRatioChange = async (val) => {
    const clamped = Math.min(100, Math.max(0, val));
    const ratio = clamped / 100;
    setSaving(true);
    try {
      await onUpdate(ben.id, {
        criteria_inputs: {
          ...ben.criteria_inputs,
          _pt_ratio: ratio,
          _pt_ratio_source: 'manual_override',
        },
      });
    } finally { setSaving(false); }
  };

  const isPartTime = ben.snapshotted_employment_type === 'part_time';
  const ptRatio = ben.criteria_inputs?._pt_ratio;
  const ptRatioPct = ptRatio != null ? (parseFloat(ptRatio) * 100) : null;
  const ptJobDays = ben.criteria_inputs?._pt_job_days;
  const ptLeaveDays = ben.criteria_inputs?._pt_leave_days;
  const ptTotalDays = ben.criteria_inputs?._pt_total_business_days;
  const ptSource = ben.criteria_inputs?._pt_ratio_source;

  const rowClass = ben.is_excluded
    ? 'opacity-50 bg-slate-50'
    : ben.is_calculated && ben.is_eligible
    ? 'bg-emerald-50/30'
    : ben.is_calculated && !ben.is_eligible
    ? 'bg-red-50/30'
    : '';

  const incentiveAmount = ben.final_incentive_amount;
  const score = ben.total_weighted_score;

  return (
    <>
      <tr className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${rowClass}`}
          data-testid={`beneficiary-row-${ben.id}`}>

        {/* Name / Role */}
        <td className="px-3 py-2.5 whitespace-nowrap">
          <div className="font-medium text-slate-900 text-sm">{ben.name || '—'}</div>
          <div className="text-xs text-slate-400">{ben.email}</div>
        </td>
        <td className="px-3 py-2.5 text-center">
          <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
            {ben.snapshotted_role_type || '—'}
          </span>
        </td>
        <td className="px-3 py-2.5 text-center text-xs text-slate-500 whitespace-nowrap">
          {ben.snapshotted_employment_type?.replace('_', ' ') || '—'}
        </td>

        {/* Part-Time Ratio (only for part-time) */}
        {hasPartTime && (
          <td className="px-3 py-2.5 bg-orange-50/30">
            {isPartTime ? (
              <div className="flex flex-col gap-0.5">
                <EditableCell
                  value={ptRatioPct}
                  onSave={handlePtRatioChange}
                  readOnly={readOnly || ben.is_excluded}
                  prefix=""
                  suffix="%"
                  placeholder="—"
                />
                <div className="flex items-center gap-1">
                  <SourceBadge source={ptSource} />
                  {ptJobDays != null && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="text-[10px] text-slate-400 cursor-help">
                            {ben.criteria_inputs?._pt_working_days || 0}/{ptTotalDays || 0}d
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">
                            {ptJobDays} job day{ptJobDays !== 1 ? 's' : ''} + {ptLeaveDays || 0} leave day{(ptLeaveDays || 0) !== 1 ? 's' : ''} = {ben.criteria_inputs?._pt_working_days || 0} working days<br/>
                            out of {ptTotalDays || 0} business days in period
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-xs text-slate-300">100%</span>
            )}
          </td>
        )}

        {/* System-derived: Revenue (auditor only) */}
        {!isLeadershipRun && (
          <td className="px-3 py-2.5 bg-blue-50/30">
            <div className="flex flex-col gap-0.5">
              <EditableCell
                value={ben.revenue_amount}
                onSave={handleRevenueChange}
                readOnly={readOnly || ben.is_excluded}
                prefix="£"
                placeholder="no data"
              />
              <div className="flex items-center gap-1">
                <SourceBadge source={ben.revenue_source} />
                {ben.revenue_job_count > 0 && (
                  <span className="text-xs text-slate-400">{ben.revenue_job_count} job{ben.revenue_job_count !== 1 ? 's' : ''}</span>
                )}
              </div>
            </div>
          </td>
        )}

        {/* System-derived: On-time % (auditor only) */}
        {!isLeadershipRun && (
          <td className="px-3 py-2.5 bg-blue-50/30">
            <div className="flex flex-col gap-0.5">
              <EditableCell
                value={ben.on_time_pct}
                onSave={handleOnTimeChange}
                readOnly={readOnly || ben.is_excluded}
                prefix=""
                suffix="%"
                placeholder="no data"
              />
              <div className="flex items-center gap-1">
                <SourceBadge source={ben.on_time_source} />
                {ben.on_time_total_jobs > 0 && (
                  <span className="text-xs text-slate-400">
                    {ben.on_time_completed_jobs}/{ben.on_time_total_jobs}
                  </span>
                )}
              </div>
            </div>
          </td>
        )}

        {/* Manual criteria inputs */}
        {manualCriteria.map(c => (
          <td key={c.key} className="px-3 py-2.5 bg-purple-50/30">
            <div className="flex flex-col gap-0.5">
              {c.scoring_type === 'categorical' ? (
                <CategoricalCell
                  criterion={c}
                  value={ben.criteria_inputs?.[c.key] ?? null}
                  onSave={(v) => handleManualCriterionChange(c.key, v)}
                  readOnly={readOnly || ben.is_excluded}
                />
              ) : c.scoring_type === 'factor' ? (
                <EditableCell
                  value={ben.criteria_inputs?.[c.key] != null ? parseFloat(ben.criteria_inputs[c.key]) * 100 : null}
                  onSave={(v) => handleManualCriterionChange(c.key, v / 100)}
                  readOnly={readOnly || ben.is_excluded}
                  prefix=""
                  suffix="%"
                  placeholder="e.g. 85"
                />
              ) : (
                <EditableCell
                  value={ben.criteria_inputs?.[c.key] ?? null}
                  onSave={(v) => handleManualCriterionChange(c.key, v)}
                  readOnly={readOnly || ben.is_excluded}
                  prefix=""
                  suffix="%"
                  placeholder="enter 0-100"
                />
              )}
              <span className={`text-xs px-1.5 py-0.5 rounded-full inline-block w-fit border ${
                c.component === 'quarterly'
                  ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
                  : 'bg-purple-50 text-purple-600 border-purple-200'
              }`}>
                {c.scoring_type === 'factor' ? 'Factor' : 'Manual'}
              </span>
            </div>
          </td>
        ))}

        {/* Override flags */}
        {overrides.map(o => (
          <td key={o.key} className="px-3 py-2.5 text-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 accent-red-500 cursor-pointer"
                    checked={!!ben.override_flags?.[o.key]}
                    disabled={readOnly || ben.is_excluded}
                    onChange={(e) => !readOnly && !ben.is_excluded && handleOverrideFlagChange(o.key, e.target.checked)}
                    data-testid={`override-${o.key}-${ben.id}`}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">{o.description || o.label}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Effect: {o.effect?.replace(/_/g, ' ')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </td>
        ))}

        {/* Calculated: Score */}
        <td className="px-3 py-2.5 text-center bg-slate-50/50">
          {ben.is_calculated ? (
            <span className={`font-semibold text-sm ${score >= 70 ? 'text-emerald-700' : score >= 40 ? 'text-amber-700' : 'text-red-600'}`}>
              {formatPct(score)}
            </span>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </td>

        {/* Leadership: Monthly / Periodic split */}
        {isLeadershipRun && (
          <td className="px-3 py-2.5 text-center bg-indigo-50/30">
            {ben.snapshotted_role_type === 'leadership' && ben.is_calculated && ben.calc_result ? (
              <div className="space-y-0.5">
                <div className="text-xs">
                  <span className="text-slate-500">M:</span>{' '}
                  <span className="font-medium text-slate-700">{formatCurrency(ben.calc_result.monthly_payout)}</span>
                </div>
                <div className="text-xs">
                  <span className="text-indigo-500">P:</span>{' '}
                  <span className="font-medium text-indigo-700">
                    {ben.calc_result.is_assessment_month
                      ? formatCurrency(ben.calc_result.periodic_payout)
                      : 'deferred'
                    }
                  </span>
                </div>
              </div>
            ) : ben.snapshotted_role_type !== 'leadership' ? (
              <span className="text-xs text-slate-300">n/a</span>
            ) : <span className="text-slate-300 text-xs">—</span>}
          </td>
        )}

        {/* Calculated: Final Amount */}
        <td className="px-3 py-2.5 text-right bg-slate-50/50">
          {ben.is_calculated && !ben.is_excluded ? (
            <div>
              <div className="font-semibold text-sm text-slate-900">{formatCurrency(incentiveAmount)}</div>
              {ben.base_incentive_amount !== ben.final_incentive_amount && (
                <div className="text-xs text-slate-400 line-through">{formatCurrency(ben.base_incentive_amount)}</div>
              )}
            </div>
          ) : <span className="text-slate-300 text-xs">—</span>}
        </td>

        {/* Eligibility */}
        <td className="px-3 py-2.5 text-center">
          <EligibilityBadge
            isEligible={ben.is_eligible}
            isCalculated={ben.is_calculated}
            isExcluded={ben.is_excluded}
          />
          {ben.is_calculated && ben.eligibility_notes && !ben.is_eligible && !ben.is_excluded && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertCircle className="h-3 w-3 text-red-400 inline-block ml-1 cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">{ben.eligibility_notes}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </td>

        {/* Actions */}
        <td className="px-3 py-2.5 text-right whitespace-nowrap">
          <div className="flex items-center gap-1 justify-end">
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
            {ben.is_calculated && (
              <button
                onClick={() => setShowDetail(v => !v)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded"
                title="Show calculation detail"
                data-testid={`detail-toggle-${ben.id}`}
              >
                {showDetail ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
            )}
            {!readOnly && (
              <button
                onClick={() => onExcludeToggle(ben.id, !ben.is_excluded)}
                className={`p-1 rounded text-xs ${ben.is_excluded ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-400 hover:text-amber-600'}`}
                title={ben.is_excluded ? 'Include beneficiary' : 'Exclude from this run'}
                data-testid={`exclude-btn-${ben.id}`}
              >
                {ben.is_excluded ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              </button>
            )}
            {!readOnly && isAdmin && (
              <button
                onClick={() => onDelete(ben.id)}
                className="p-1 rounded text-slate-300 hover:text-red-500"
                title="Remove from run"
                data-testid={`delete-ben-btn-${ben.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </td>
      </tr>

      {/* Calculation detail drawer */}
      {showDetail && ben.calc_result && (
        <tr className="border-b border-slate-100 bg-slate-50">
          <td colSpan={99} className="px-4 py-3">
            <div className="space-y-2">
              <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Calculation Breakdown</div>
              <div className="grid grid-cols-1 gap-1">
                {(ben.calc_result.criteria_scores || []).map(cs => (
                  <div key={cs.key} className="flex items-center gap-3 text-xs">
                    <span className="w-36 text-slate-600 font-medium">{cs.label}</span>
                    <span className="w-20 text-slate-500">
                      {cs.key === 'revenue' ? formatCurrency(cs.raw_value) : cs.raw_value !== null ? `${cs.raw_value}%` : '—'}
                    </span>
                    <div className="w-24 bg-slate-200 rounded-full h-1.5">
                      <div
                        className={`h-1.5 rounded-full ${cs.score_pct >= 70 ? 'bg-emerald-500' : cs.score_pct >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                        style={{ width: `${cs.score_pct}%` }}
                      />
                    </div>
                    <span className="text-slate-700 w-12">{cs.score_pct?.toFixed(1)}%</span>
                    <span className="text-slate-400">× {cs.weight_pct}% weight</span>
                    <span className="text-slate-700 font-medium w-16">= {cs.weighted_score?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              {ben.calc_result.overrides_applied?.length > 0 && (
                <div className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                  Overrides applied: {ben.calc_result.overrides_applied.map(o => o.label).join(', ')}
                </div>
              )}
              {/* iter507 — Net Profit gate indicator */}
              {ben.calc_result.net_profit_gate_applied && (
                <div
                  className={`text-xs px-2 py-1 rounded border ${
                    ben.calc_result.net_profit_gate_applied.triggered
                      ? 'text-amber-800 bg-amber-50 border-amber-200'
                      : 'text-emerald-800 bg-emerald-50 border-emerald-200'
                  }`}
                  data-testid={`net-profit-gate-${ben.id}`}
                >
                  {ben.calc_result.net_profit_gate_applied.triggered ? (
                    <>
                      <span className="font-semibold">⚠ Net Profit gate triggered.</span>{' '}
                      Month £{Number(ben.calc_result.net_profit_gate_applied.net_profit_for_month).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' '}&lt; threshold £{Number(ben.calc_result.net_profit_gate_applied.threshold).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' '}→ reduced by {ben.calc_result.net_profit_gate_applied.reduction_pct}%
                      {' '}(£{Number(ben.calc_result.net_profit_gate_applied.pre_gate_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' '}→ £{Number(ben.calc_result.net_profit_gate_applied.post_gate_amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                      {ben.calc_result.net_profit_gate_applied.net_profit_source === 'missing' && (
                        <span className="ml-1 italic">— no Leadership run for month, treated as £0</span>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">✓ Net Profit gate passed.</span>{' '}
                      Month £{Number(ben.calc_result.net_profit_gate_applied.net_profit_for_month).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' '}≥ threshold £{Number(ben.calc_result.net_profit_gate_applied.threshold).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {' '}— full incentive
                    </>
                  )}
                </div>
              )}
              {ben.calculation_notes && (
                <div className="text-xs text-slate-500 italic">{ben.calculation_notes}</div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
});

// ─────────────────────────────────────────────────────
// Leadership Shared Group Inputs
// ─────────────────────────────────────────────────────

function LeadershipSharedInputs({ runId, sharedInputs, sharedKeys, criteria, readOnly, onUpdate, isAssessmentMonth, periodEndMonth }) {
  const [localInputs, setLocalInputs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const initial = {};
    sharedKeys.forEach(k => {
      initial[k] = sharedInputs[k] ?? '';
    });
    setLocalInputs(initial);
  }, [sharedInputs, sharedKeys]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const inputs = {};
      sharedKeys.forEach(k => {
        const val = parseFloat(localInputs[k]);
        if (!isNaN(val)) inputs[k] = val;
      });
      await onUpdate(inputs);
    } finally {
      setSaving(false);
    }
  };

  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <Card data-testid="leadership-shared-inputs-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-indigo-600" />
            <CardTitle className="text-sm">Group Inputs (applied to all leaders)</CardTitle>
            {periodEndMonth && (
              <Badge className={isAssessmentMonth
                ? 'bg-indigo-100 text-indigo-700 border-indigo-200 text-xs'
                : 'bg-slate-100 text-slate-500 border-slate-200 text-xs'
              }>
                Period: {monthNames[periodEndMonth - 1]} {isAssessmentMonth ? '(Assessment)' : ''}
              </Badge>
            )}
          </div>
          {!readOnly && (
            <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 text-xs"
              data-testid="save-shared-inputs-btn">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              Save & Propagate
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-slate-500 mb-3">
          These values are entered once and applied to every leadership beneficiary in this run.
          After saving, re-run "Calculate All" to update results.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {criteria.map(c => (
            <div key={c.key} className="space-y-1.5">
              <Label className="text-sm">
                {c.label}
                <span className="text-xs text-slate-400 ml-1">({c.weight_pct}% weight)</span>
              </Label>
              {c.scoring_type === 'banded' && (c.bands || []).length > 0 && (
                <div className="text-[10px] text-slate-400">
                  Bands: {c.bands.map((b, i) => `≥£${Number(b.threshold).toLocaleString()} → ${b.score_pct}%`).join(' | ')}
                </div>
              )}
              <Input
                type="number"
                step="0.01"
                value={localInputs[c.key] ?? ''}
                onChange={e => setLocalInputs(p => ({ ...p, [c.key]: e.target.value }))}
                placeholder={`Enter ${c.label.toLowerCase()} value`}
                disabled={readOnly}
                data-testid={`shared-input-${c.key}`}
              />
              {c.description && (
                <p className="text-xs text-slate-400">{c.description}</p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────
// Workspace Section
// ─────────────────────────────────────────────────────

function WorkspaceSection({ runId, runStatus, incentiveTypeName }) {
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();

  const canManage = isAdmin || isSeniorScheduler;
  const isReadOnly = runStatus === 'published';

  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [deleteBenId, setDeleteBenId] = useState(null);
  const [excludeDialogInfo, setExcludeDialogInfo] = useState(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const fetchWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/incentives/runs/${runId}/workspace`);
      setWorkspace(res.data);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load workspace.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [runId, toast]);

  useEffect(() => { fetchWorkspace(); }, [fetchWorkspace]);

  const handleInitialize = async (force = false) => {
    setInitializing(true);
    try {
      const res = await api.post(`/incentives/runs/${runId}/workspace/initialize?force_reinit=${force}`);
      toast({ title: 'Workspace loaded', description: res.data.message });
      await fetchWorkspace();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to initialize.'), variant: 'destructive' });
    } finally {
      setInitializing(false);
    }
  };

  const handleCalculateAll = async () => {
    setCalculating(true);
    try {
      const res = await api.post(`/incentives/runs/${runId}/workspace/calculate`);
      toast({ title: 'Calculated', description: res.data.message });
      await fetchWorkspace();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Calculation failed.'), variant: 'destructive' });
    } finally {
      setCalculating(false);
    }
  };

  const handleUpdateBeneficiary = async (benId, payload) => {
    try {
      const res = await api.put(`/incentives/runs/${runId}/workspace/beneficiaries/${benId}`, payload);
      setWorkspace(prev => ({
        ...prev,
        beneficiaries: prev.beneficiaries.map(b => b.id === benId ? res.data : b),
      }));
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Save failed.'), variant: 'destructive' });
      throw err;
    }
  };

  const handleExcludeToggle = async (benId, exclude) => {
    if (exclude) {
      setExcludeDialogInfo({ benId, exclude });
    } else {
      await doExclude(benId, false, '');
    }
  };

  const doExclude = async (benId, isExcluded, reason) => {
    try {
      const res = await api.patch(`/incentives/runs/${runId}/workspace/beneficiaries/${benId}/exclude`, {
        is_excluded: isExcluded,
        exclusion_reason: reason || null,
      });
      setWorkspace(prev => ({
        ...prev,
        beneficiaries: prev.beneficiaries.map(b => b.id === benId ? res.data : b),
        summary: {
          ...prev.summary,
          excluded: prev.beneficiaries.filter(b => (b.id === benId ? isExcluded : b.is_excluded)).length,
        },
      }));
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed.'), variant: 'destructive' });
    } finally {
      setExcludeDialogInfo(null);
    }
  };

  const handleDeleteBeneficiary = async () => {
    if (!deleteBenId) return;
    try {
      await api.delete(`/incentives/runs/${runId}/workspace/beneficiaries/${deleteBenId}`);
      setWorkspace(prev => ({
        ...prev,
        beneficiaries: prev.beneficiaries.filter(b => b.id !== deleteBenId),
      }));
      toast({ title: 'Removed', description: 'Beneficiary removed from run.' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Delete failed.'), variant: 'destructive' });
    } finally {
      setDeleteBenId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const { schemes = [], beneficiaries = [], summary = {} } = workspace || {};
  const run = workspace?.run || {};
  const hasSchemes = schemes.length > 0;
  const hasLoaded = beneficiaries.some(b => b.data_loaded_at);

  // Determine the "primary" scheme to drive column structure
  const primaryScheme = schemes.find(s => s.role_type === 'auditor') || schemes[0] || null;
  const leadershipScheme = schemes.find(s => s.role_type === 'leadership') || null;
  // Leadership run if there are leadership beneficiaries and a leadership scheme with config
  const hasLeadershipBens = beneficiaries.some(b => b.snapshotted_role_type === 'leadership');
  const hasAuditorBens = beneficiaries.some(b => b.snapshotted_role_type === 'auditor');
  const isLeadershipRun = hasLeadershipBens && leadershipScheme?.leadership_config;
  const leadershipConfig = leadershipScheme?.leadership_config || null;
  const sharedKeys = leadershipConfig?.shared_criteria_keys || [];
  const assessmentMonths = leadershipConfig?.assessment_months || [3, 6, 9, 12];
  const periodEndMonth = run.period_end ? new Date(run.period_end).getMonth() + 1 : null;
  const isAssessmentMonth = periodEndMonth && assessmentMonths.includes(periodEndMonth);

  // Identify manual criteria (non-system-derived)
  // For leadership-only: per-person criteria are quarterly ones; shared inputs handle monthly
  // For mixed: show auditor-style table (leadership bens get their own handling in rows)
  const pureLeadership = isLeadershipRun && !hasAuditorBens;
  const effectiveScheme = pureLeadership ? leadershipScheme : primaryScheme;
  const manualCriteria = effectiveScheme
    ? (effectiveScheme.criteria || []).filter(c => {
        if (pureLeadership) {
          return c.component === 'quarterly';
        }
        return !_SYSTEM_KEYS.has(c.key);
      })
    : [];
  const overridesCols = effectiveScheme ? (effectiveScheme.overrides || []) : [];

  // Recalc summary from live data
  const liveEligible = beneficiaries.filter(b => b.is_eligible && !b.is_excluded).length;
  const liveExcluded = beneficiaries.filter(b => b.is_excluded).length;
  const liveTotalAmount = beneficiaries
    .filter(b => !b.is_excluded && b.is_calculated)
    .reduce((s, b) => s + (parseFloat(b.final_incentive_amount) || 0), 0);
  const liveCalculated = beneficiaries.filter(b => b.is_calculated).length;
  const liveIneligible = beneficiaries.filter(b => b.is_calculated && !b.is_eligible && !b.is_excluded).length;
  const liveOverridesApplied = beneficiaries.filter(b =>
    b.override_flags && Object.values(b.override_flags).some(v => v)
  ).length;

  // Leaderboard: sorted by score descending (active calculated only)
  const leaderboard = beneficiaries
    .filter(b => b.is_calculated && !b.is_excluded)
    .sort((a, b) => (parseFloat(b.total_weighted_score) || 0) - (parseFloat(a.total_weighted_score) || 0));

  // Check if any beneficiary is part-time (to show PT ratio column)
  const hasPartTime = beneficiaries.some(b => b.snapshotted_employment_type === 'part_time');

  return (
    <div className="space-y-4" data-testid="incentive-workspace">
      {/* ── Workspace Header ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-slate-600" />
              <CardTitle className="text-base">Run Workspace</CardTitle>
              {hasLoaded && (
                <Badge variant="outline" className="text-slate-500 text-xs">
                  {beneficiaries.length} beneficiar{beneficiaries.length !== 1 ? 'ies' : 'y'}
                </Badge>
              )}
            </div>
            {!isReadOnly && canManage && (
              <div className="flex items-center gap-2 flex-wrap">
                {hasLoaded && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleInitialize(false)}
                    disabled={initializing || calculating}
                    className="gap-1.5 text-xs"
                    data-testid="reload-system-btn"
                  >
                    {initializing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Refresh System Data
                  </Button>
                )}
                {!hasLoaded && (
                  <Button
                    size="sm"
                    onClick={() => handleInitialize(false)}
                    disabled={initializing}
                    className="gap-1.5"
                    data-testid="initialize-workspace-btn"
                  >
                    {initializing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
                    {initializing ? 'Loading…' : 'Load Beneficiaries from System'}
                  </Button>
                )}
                {hasLoaded && (
                  <Button
                    size="sm"
                    onClick={handleCalculateAll}
                    disabled={calculating || initializing}
                    className="gap-1.5 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="calculate-all-btn"
                  >
                    {calculating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
                    {calculating ? 'Calculating…' : 'Calculate All'}
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardHeader>

        {/* Scheme status / warnings */}
        {!hasSchemes && (
          <CardContent className="pt-0">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-medium">No active scheme configured</span> for incentive type "{incentiveTypeName}".{' '}
                <Link to="/incentives/schemes" className="underline hover:text-amber-900">Configure a scheme</Link> before running calculations.
              </div>
            </div>
          </CardContent>
        )}

        {/* Summary metrics */}
        {hasLoaded && liveCalculated > 0 && (
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Beneficiaries', value: beneficiaries.length, color: 'text-slate-700' },
                { label: 'Eligible', value: liveEligible, color: 'text-emerald-700' },
                { label: 'Ineligible', value: liveIneligible, color: liveIneligible > 0 ? 'text-red-600' : 'text-slate-500' },
                { label: 'Excluded', value: liveExcluded, color: liveExcluded > 0 ? 'text-amber-600' : 'text-slate-500' },
                { label: 'Overrides', value: liveOverridesApplied, color: liveOverridesApplied > 0 ? 'text-amber-600' : 'text-slate-500' },
                { label: 'Total Payout', value: formatCurrency(liveTotalAmount), color: 'text-indigo-700 font-semibold' },
              ].map(item => (
                <div key={item.label} className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <div className={`text-lg font-bold ${item.color}`}>{item.value}</div>
                  <div className="text-xs text-slate-500">{item.label}</div>
                </div>
              ))}
            </div>
            {/* Leaderboard toggle */}
            {leaderboard.length > 0 && (
              <div className="mt-3">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-xs"
                  onClick={() => setShowLeaderboard(!showLeaderboard)}
                  data-testid="toggle-leaderboard-btn"
                >
                  <TrendingUp className="h-3.5 w-3.5" />
                  {showLeaderboard ? 'Hide' : 'Show'} Leaderboard
                  {showLeaderboard ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ── Scheme Summary ── */}
      {hasSchemes && effectiveScheme && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 font-medium">Active Scheme Configuration</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
              {effectiveScheme?.revenue_threshold && (
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400">Revenue threshold:</span>
                  <span className="font-semibold text-slate-700">{formatCurrency(primaryScheme.revenue_threshold)}</span>
                  <span className="text-slate-400 text-[10px]">(eligibility gate)</span>
                </div>
              )}
              {effectiveScheme?.on_time_threshold_pct && (
                <div className="flex items-center gap-1.5">
                  <span className="text-slate-400">On-time threshold:</span>
                  <span className="font-semibold text-slate-700">{formatPct(primaryScheme.on_time_threshold_pct)}</span>
                  <span className="text-slate-400 text-[10px]">(eligibility gate)</span>
                </div>
              )}
              {/* iter507 — Net Profit gate summary */}
              {effectiveScheme?.role_type === 'auditor' && effectiveScheme?.net_profit_gate?.enabled && (
                <div
                  className="flex items-center gap-1.5"
                  data-testid="net-profit-gate-summary"
                >
                  <span className="text-slate-400">Net Profit gate:</span>
                  <span className="font-semibold text-slate-700">
                    &lt; {formatCurrency(effectiveScheme.net_profit_gate.threshold)}
                  </span>
                  <span className="text-slate-400 text-[10px]">
                    (−{effectiveScheme.net_profit_gate.reduction_pct}% if below)
                  </span>
                </div>
              )}
              {(effectiveScheme?.criteria || []).map(c => (
                <div key={c.key} className="flex items-center gap-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    c.component === 'quarterly'         ? 'bg-indigo-100 text-indigo-700'
                    : _SYSTEM_KEYS.has(c.key)           ? 'bg-blue-100 text-blue-700'
                    : c.scoring_type === 'categorical'  ? 'bg-teal-100 text-teal-700'
                    : c.scoring_type === 'banded'       ? 'bg-orange-100 text-orange-700'
                    : c.scoring_type === 'factor'       ? 'bg-violet-100 text-violet-700'
                    : 'bg-purple-100 text-purple-700'
                  }`}>
                    {c.component === 'quarterly' ? 'Periodic'
                     : _SYSTEM_KEYS.has(c.key)          ? 'System'
                     : c.scoring_type === 'categorical' ? 'Categorical'
                     : c.scoring_type === 'banded'      ? 'Banded'
                     : c.scoring_type === 'factor'      ? 'Factor'
                     : 'Manual'}
                  </span>
                  <span>{c.label} <strong>{c.weight_pct}%</strong></span>
                </div>
              ))}
            </div>
            <div className="mt-2 text-xs text-slate-500">
              {isLeadershipRun
                ? 'Leadership: monthly payout = monthly criteria score × cap; periodic payout = periodic score × (cap × accrual months)'
                : 'Incentive = (total score / 100) × monthly limit per beneficiary'
              }
            </div>
            {isLeadershipRun && (
              <div className="mt-1 text-xs text-indigo-600">
                Assessment months: {assessmentMonths.map(m => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m-1]).join(', ')}
                {periodEndMonth && (
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium ${isAssessmentMonth ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
                    {isAssessmentMonth ? 'This run IS an assessment month' : 'This run is NOT an assessment month'}
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Leadership Shared Group Inputs ── */}
      {isLeadershipRun && hasLoaded && (
        <LeadershipSharedInputs
          runId={runId}
          sharedInputs={run.shared_inputs || {}}
          sharedKeys={sharedKeys}
          criteria={(leadershipScheme?.criteria || []).filter(c => sharedKeys.includes(c.key))}
          readOnly={isReadOnly || !canManage}
          onUpdate={async (inputs) => {
            try {
              await api.put(`/incentives/runs/${runId}/workspace/shared-inputs`, { inputs });
              toast({ title: 'Updated', description: 'Shared inputs saved and propagated to all beneficiaries.' });
              await fetchWorkspace();
            } catch (err) {
              toast({ title: 'Error', description: getApiError(err, 'Failed to save shared inputs.'), variant: 'destructive' });
            }
          }}
          isAssessmentMonth={isAssessmentMonth}
          periodEndMonth={periodEndMonth}
        />
      )}

      {/* ── Leaderboard (collapsible) ── */}
      {showLeaderboard && leaderboard.length > 0 && (
        <Card data-testid="leaderboard-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-600 font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Leaderboard — Ranked by Score
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="leaderboard-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                    <th className="px-3 py-2 text-center w-12">Rank</th>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-center">Role</th>
                    <th className="px-3 py-2 text-center">Score</th>
                    <th className="px-3 py-2 text-right">Incentive</th>
                    <th className="px-3 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboard.map((ben, idx) => {
                    const rank = idx + 1;
                    const rankBg = rank === 1 ? 'bg-amber-50' : rank === 2 ? 'bg-slate-50' : rank === 3 ? 'bg-orange-50/50' : '';
                    return (
                      <tr key={ben.id} className={`border-b border-slate-100 ${rankBg}`} data-testid={`leaderboard-row-${ben.id}`}>
                        <td className="px-3 py-2 text-center">
                          {rank <= 3 ? (
                            <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-xs font-bold ${
                              rank === 1 ? 'bg-amber-200 text-amber-800' :
                              rank === 2 ? 'bg-slate-200 text-slate-700' :
                              'bg-orange-200 text-orange-800'
                            }`}>{rank}</span>
                          ) : (
                            <span className="text-slate-400 text-xs">{rank}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">{ben.name || '—'}</td>
                        <td className="px-3 py-2 text-center">
                          <Badge variant="outline" className="text-xs capitalize">{ben.snapshotted_role_type || '—'}</Badge>
                        </td>
                        <td className="px-3 py-2 text-center font-semibold text-indigo-700">
                          {parseFloat(ben.total_weighted_score || 0).toFixed(1)}%
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">
                          {formatCurrency(ben.final_incentive_amount)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <EligibilityBadge isEligible={ben.is_eligible} isCalculated={ben.is_calculated} isExcluded={ben.is_excluded} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Beneficiary Table ── */}
      {hasLoaded && beneficiaries.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="beneficiary-table">
                <thead>
                  {/* Column group labels */}
                  <tr className="text-[10px] uppercase tracking-wide">
                    <th colSpan={hasPartTime ? 4 : 3} className="px-3 py-1.5 text-left text-slate-500 border-b border-slate-100">
                      Beneficiary
                    </th>
                    {!isLeadershipRun && (
                      <th colSpan={2} className="px-3 py-1.5 text-center text-blue-600 bg-blue-50/50 border-b border-blue-100">
                        System-derived inputs
                      </th>
                    )}
                    {manualCriteria.length > 0 && (
                      <th colSpan={manualCriteria.length} className="px-3 py-1.5 text-center text-purple-600 bg-purple-50/50 border-b border-purple-100">
                        {isLeadershipRun ? 'Periodic inputs (per person)' : 'Manual inputs'}
                      </th>
                    )}
                    {overridesCols.length > 0 && (
                      <th colSpan={overridesCols.length} className="px-3 py-1.5 text-center text-red-600 border-b border-slate-100">
                        Override flags
                      </th>
                    )}
                    <th colSpan={isLeadershipRun ? 4 : 3} className="px-3 py-1.5 text-center text-slate-500 bg-slate-50/50 border-b border-slate-100">
                      Calculated outputs
                    </th>
                    <th className="px-3 py-1.5 border-b border-slate-100" />
                  </tr>
                  {/* Column headers */}
                  <tr className="border-b-2 border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600">
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-center">Role</th>
                    <th className="px-3 py-2 text-center whitespace-nowrap">Emp. Type</th>
                    {hasPartTime && (
                      <th className="px-3 py-2 text-center bg-orange-50/30 whitespace-nowrap">
                        PT Ratio
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="h-3 w-3 inline-block ml-1 text-orange-400 cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-xs text-xs">Part-time working ratio = (job days + leave days) / business days. Pro-rates revenue thresholds and incentive limit. Editable.</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </th>
                    )}
                    {!isLeadershipRun && (
                      <>
                        <th className="px-3 py-2 text-left bg-blue-50/50 whitespace-nowrap">
                          Revenue
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 inline-block ml-1 text-blue-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-xs">Forecast revenue from approved CLIENT_JOB records in the incentive period.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </th>
                        <th className="px-3 py-2 text-left bg-blue-50/50 whitespace-nowrap">
                          On-Time %
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 inline-block ml-1 text-blue-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-xs">% of completed jobs where completion date was within 1 day of scheduled date.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </th>
                      </>
                    )}
                    {manualCriteria.map(c => (
                      <th key={c.key} className="px-3 py-2 text-left bg-purple-50/30 whitespace-nowrap">
                        {c.label}
                        {c.description && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Info className="h-3 w-3 inline-block ml-1 text-purple-400 cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="max-w-xs text-xs">{c.description}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </th>
                    ))}
                    {overridesCols.map(o => (
                      <th key={o.key} className="px-3 py-2 text-center" style={{minWidth: '80px', maxWidth: '120px'}}>
                        <span className="block text-xs leading-tight">{o.label}</span>
                      </th>
                    ))}
                    <th className="px-3 py-2 text-center bg-slate-50/50 whitespace-nowrap">Score</th>
                    {isLeadershipRun && (
                      <th className="px-3 py-2 text-center bg-indigo-50/50 whitespace-nowrap">
                        Monthly / Periodic
                      </th>
                    )}
                    <th className="px-3 py-2 text-right bg-slate-50/50 whitespace-nowrap">Incentive</th>
                    <th className="px-3 py-2 text-center bg-slate-50/50">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {beneficiaries.map(ben => {
                    // Find this beneficiary's specific scheme (could differ if multi-role)
                    const benScheme = schemes.find(s => s.id === ben.scheme_id) || primaryScheme;
                    return (
                      <BeneficiaryRow
                        key={ben.id}
                        ben={ben}
                        scheme={benScheme}
                        readOnly={isReadOnly || !canManage}
                        isAdmin={isAdmin}
                        hasPartTime={hasPartTime}
                        isLeadershipRun={isLeadershipRun}
                        onUpdate={handleUpdateBeneficiary}
                        onExcludeToggle={handleExcludeToggle}
                        onDelete={(id) => setDeleteBenId(id)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {hasLoaded && beneficiaries.length === 0 && (
        <div className="text-center py-8 text-slate-400 text-sm">
          No beneficiaries found. Ensure users have incentive settings configured.
        </div>
      )}

      {/* Exclude confirm dialog */}
      <Dialog
        open={!!excludeDialogInfo}
        onOpenChange={(open) => !open && setExcludeDialogInfo(null)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Exclude Beneficiary?</DialogTitle>
          </DialogHeader>
          <ExcludeReasonForm
            onConfirm={(reason) => doExclude(excludeDialogInfo.benId, true, reason)}
            onCancel={() => setExcludeDialogInfo(null)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete beneficiary confirm */}
      <AlertDialog open={!!deleteBenId} onOpenChange={(open) => !open && setDeleteBenId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Beneficiary?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this beneficiary from the run. You can re-initialize to add them back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBeneficiary}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-ben-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExcludeReasonForm({ onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Reason for exclusion (optional)</Label>
        <Textarea
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. On leave during period, duplicate entry…"
          data-testid="exclusion-reason-input"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => onConfirm(reason)}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="confirm-exclude-btn"
        >
          Exclude
        </Button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────
// PDF Statements + Email Section (Phase 7+8)
// ─────────────────────────────────────────────────────

function PDFStatementsSection({ runId, run }) {
  const { toast } = useToast();
  const [statements, setStatements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [emailPreview, setEmailPreview] = useState(null); // {targets: [...], mode: 'one'|'selected'|'all'}

  const fetchStatements = useCallback(async () => {
    try {
      const res = await api.get(`/incentives/runs/${runId}/statements`);
      setStatements(res.data);
    } catch (err) {
      console.error('Failed to load statements:', err);
    } finally { setLoading(false); }
  }, [runId]);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);

  // Single "Generate / Regenerate All" — async background generation
  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/incentives/runs/${runId}/statements/generate?force_regenerate=true`);
      toast({ title: 'PDF Generation Started', description: res.data.message });
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const r = await api.get(`/incentives/runs/${runId}/statements`);
          setStatements(r.data);
          const s = r.data?.summary || {};
          const generating = (r.data?.statements || []).some(st => st.status === 'generating');
          if (!generating) {
            clearInterval(poll);
            setGenerating(false);
            toast({ title: 'Generation Complete', description: `${s.ready || 0} PDFs ready.` });
          }
        } catch { clearInterval(poll); setGenerating(false); }
      }, 3000);
    } catch (err) {
      toast({ title: 'Generation Failed', description: getApiError(err, 'Failed.'), variant: 'destructive' });
      setGenerating(false);
    }
  };

  const handleGenerateSingle = async (benId) => {
    setGeneratingId(benId);
    try {
      await api.post(`/incentives/runs/${runId}/statements/${benId}/generate`);
      toast({ title: 'PDF Generated', description: 'Statement generated successfully.' });
      await fetchStatements();
    } catch (err) {
      toast({ title: 'Generation Failed', description: getApiError(err, 'Failed.'), variant: 'destructive' });
    } finally { setGeneratingId(null); }
  };

  // Preview: download blob and open in new tab (sends auth header)
  const handlePreview = async (benId) => {
    try {
      const response = await api.get(`/incentives/runs/${runId}/statements/${benId}/preview`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (err) {
      toast({ title: 'Preview Failed', description: getApiError(err, 'Could not preview PDF.'), variant: 'destructive' });
    }
  };

  const handleDownload = async (benId, fileName) => {
    try {
      const response = await api.get(`/incentives/runs/${runId}/statements/${benId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || `incentive_statement_${benId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Download Failed', description: getApiError(err, 'Failed.'), variant: 'destructive' });
    }
  };

  // Email: show preview first, then confirm to send
  const openEmailPreview = (mode, benId = null) => {
    const stmtList = statements?.statements || [];
    let targets = [];
    if (mode === 'one' && benId) {
      targets = stmtList.filter(s => s.beneficiary_id === benId);
    } else if (mode === 'selected') {
      targets = stmtList.filter(s => selected.has(s.beneficiary_id) && s.status === 'ready' && !s.is_excluded);
    } else {
      targets = stmtList.filter(s => s.status === 'ready' && !s.is_excluded && s.email);
    }
    setEmailPreview({ targets, mode, benId });
  };

  const confirmSendEmails = async () => {
    if (!emailPreview) return;
    const { mode, benId, targets } = emailPreview;
    setEmailPreview(null);

    if (mode === 'one' && benId) {
      setSendingId(benId);
      try {
        const res = await api.post(`/incentives/runs/${runId}/statements/email/${benId}`);
        toast({ title: 'Email Sent', description: `Statement emailed to ${res.data.email}.` });
        await fetchStatements();
      } catch (err) {
        toast({ title: 'Email Failed', description: getApiError(err, 'Failed.'), variant: 'destructive' });
      } finally { setSendingId(null); }
    } else {
      setSendingAll(true);
      const ids = mode === 'selected' ? [...selected] : [];
      try {
        const res = await api.post(`/incentives/runs/${runId}/statements/email-bulk`, { beneficiary_ids: ids });
        toast({ title: 'Emails Sent', description: res.data.message });
        if (mode === 'selected') setSelected(new Set());
        await fetchStatements();
      } catch (err) {
        toast({ title: 'Email Failed', description: getApiError(err, 'Failed.'), variant: 'destructive' });
      } finally { setSendingAll(false); }
    }
  };

  const toggleSelect = (benId) => {
    setSelected(prev => { const n = new Set(prev); n.has(benId) ? n.delete(benId) : n.add(benId); return n; });
  };

  const toggleSelectAll = () => {
    const readyIds = (statements?.statements || []).filter(s => s.status === 'ready' && !s.is_excluded).map(s => s.beneficiary_id);
    setSelected(prev => prev.size === readyIds.length ? new Set() : new Set(readyIds));
  };

  if (loading) {
    return (
      <Card data-testid="pdf-statements-card">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" />PDF Statements &amp; Email</CardTitle></CardHeader>
        <CardContent><div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></CardContent>
      </Card>
    );
  }

  const { summary = {}, statements: stmtList = [] } = statements || {};
  const readyCount = stmtList.filter(s => s.status === 'ready' && !s.is_excluded).length;

  return (
    <>
    <Card data-testid="pdf-statements-card">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          PDF Statements &amp; Email
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={handleGenerateAll} disabled={generating} data-testid="generate-all-pdfs-btn">
            {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            {summary.ready > 0 ? 'Regenerate All PDFs' : 'Generate All PDFs'}
          </Button>
          {selected.size > 0 && (
            <Button size="sm" onClick={() => openEmailPreview('selected')} disabled={sendingAll} className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="send-selected-emails-btn">
              {sendingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Email Selected ({selected.size})
            </Button>
          )}
          {readyCount > 0 && selected.size === 0 && (
            <Button size="sm" variant="outline" onClick={() => openEmailPreview('all')} disabled={sendingAll} className="gap-1.5 text-blue-700 border-blue-300" data-testid="send-all-emails-btn">
              {sendingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Email All
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
            <div className="text-xl font-semibold text-slate-900">{summary.total || 0}</div>
            <div className="text-[10px] text-slate-500">Total</div>
          </div>
          <div className="bg-emerald-50 rounded-lg p-2.5 text-center">
            <div className="text-xl font-semibold text-emerald-700">{summary.ready || 0}</div>
            <div className="text-[10px] text-emerald-600">PDF Ready</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-2.5 text-center">
            <div className="text-xl font-semibold text-slate-600">{(summary.pending || 0) + (summary.not_generated || 0)}</div>
            <div className="text-[10px] text-slate-500">Not Generated</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-2.5 text-center">
            <div className="text-xl font-semibold text-blue-700">{summary.email_sent || 0}</div>
            <div className="text-[10px] text-blue-600">Emailed</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-2.5 text-center">
            <div className="text-xl font-semibold text-amber-700">{summary.email_not_sent || 0}</div>
            <div className="text-[10px] text-amber-600">Not Sent</div>
          </div>
          <div className="bg-red-50 rounded-lg p-2.5 text-center">
            <div className="text-xl font-semibold text-red-700">{(summary.failed || 0) + (summary.email_failed || 0)}</div>
            <div className="text-[10px] text-red-600">Failed</div>
          </div>
        </div>

        {/* Statements Table */}
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="w-8 px-2 py-2">
                  <input type="checkbox" className="h-3.5 w-3.5 rounded" checked={selected.size > 0 && selected.size === readyCount} onChange={toggleSelectAll} data-testid="select-all-checkbox" />
                </th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Beneficiary</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600">PDF</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600">Email</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600">Amount</th>
                <th className="text-center px-3 py-2 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stmtList.map((stmt) => {
                const isReady = stmt.status === 'ready';
                const canEmail = isReady && !stmt.is_excluded && stmt.email;
                return (
                  <tr key={stmt.beneficiary_id} className={`border-b last:border-b-0 ${stmt.is_excluded ? 'opacity-50 bg-slate-50' : ''}`} data-testid={`pdf-stmt-row-${stmt.beneficiary_id}`}>
                    <td className="px-2 py-2 text-center">
                      {isReady && !stmt.is_excluded && (
                        <input type="checkbox" className="h-3.5 w-3.5 rounded" checked={selected.has(stmt.beneficiary_id)} onChange={() => toggleSelect(stmt.beneficiary_id)} />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{stmt.name || '—'}</div>
                      <div className="text-xs text-slate-400">{stmt.email || ''}{stmt.is_excluded ? ' (Excluded)' : ''}</div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {isReady && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle2 className="h-3 w-3 mr-1" />Ready</Badge>}
                      {stmt.status === 'not_generated' && <Badge variant="outline" className="text-slate-400 border-slate-200">Not Generated</Badge>}
                      {stmt.status === 'failed' && (
                        <TooltipProvider><Tooltip><TooltipTrigger>
                          <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>
                        </TooltipTrigger><TooltipContent><p className="text-xs max-w-xs">{stmt.error_message}</p></TooltipContent></Tooltip></TooltipProvider>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {stmt.email_status === 'sent' && (
                        <TooltipProvider><Tooltip><TooltipTrigger>
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200"><CheckCircle2 className="h-3 w-3 mr-1" />Sent</Badge>
                        </TooltipTrigger><TooltipContent><p className="text-xs">{stmt.email_sent_at ? formatDateTime(stmt.email_sent_at) : ''}{stmt.email_send_count > 1 ? ` (${stmt.email_send_count}x)` : ''}</p></TooltipContent></Tooltip></TooltipProvider>
                      )}
                      {stmt.email_status === 'failed' && (
                        <TooltipProvider><Tooltip><TooltipTrigger>
                          <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50"><AlertCircle className="h-3 w-3 mr-1" />Failed</Badge>
                        </TooltipTrigger><TooltipContent><p className="text-xs max-w-xs">{stmt.email_error_message}</p></TooltipContent></Tooltip></TooltipProvider>
                      )}
                      {stmt.email_status === 'not_sent' && isReady && <span className="text-xs text-slate-400">Not sent</span>}
                    </td>
                    <td className="px-3 py-2 text-center font-medium">
                      {stmt.is_eligible ? formatCurrency(stmt.final_amount) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-0.5">
                        {isReady && (
                          <>
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handlePreview(stmt.beneficiary_id)} data-testid={`preview-pdf-${stmt.beneficiary_id}`}><ExternalLink className="h-3.5 w-3.5" /></Button>
                            </TooltipTrigger><TooltipContent>Preview</TooltipContent></Tooltip></TooltipProvider>
                            <TooltipProvider><Tooltip><TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDownload(stmt.beneficiary_id, stmt.file_name)} data-testid={`download-pdf-${stmt.beneficiary_id}`}><Download className="h-3.5 w-3.5" /></Button>
                            </TooltipTrigger><TooltipContent>Download</TooltipContent></Tooltip></TooltipProvider>
                            {canEmail && (
                              <TooltipProvider><Tooltip><TooltipTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600" onClick={() => openEmailPreview('one', stmt.beneficiary_id)} disabled={sendingId === stmt.beneficiary_id} data-testid={`send-email-${stmt.beneficiary_id}`}>
                                  {sendingId === stmt.beneficiary_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                </Button>
                              </TooltipTrigger><TooltipContent>{stmt.email_status === 'sent' ? 'Resend' : 'Send'} Email</TooltipContent></Tooltip></TooltipProvider>
                            )}
                          </>
                        )}
                        {!isReady && (
                          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleGenerateSingle(stmt.beneficiary_id)} disabled={generatingId === stmt.beneficiary_id} data-testid={`generate-pdf-${stmt.beneficiary_id}`}>
                            {generatingId === stmt.beneficiary_id ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <FileText className="h-3.5 w-3.5 mr-1" />}
                            Generate
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {stmtList.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">No beneficiaries found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {/* Email Preview Modal */}
    <Dialog open={!!emailPreview} onOpenChange={(open) => !open && setEmailPreview(null)}>
      <DialogContent
        className="overflow-y-auto dialog-resizable"
        style={{ width: '42rem', maxHeight: '85vh' }}
        data-testid="incentive-email-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle>Email Preview & Confirm</DialogTitle>
        </DialogHeader>
        {emailPreview && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              {emailPreview.targets.length === 1
                ? `Send incentive statement to ${emailPreview.targets[0]?.name}?`
                : `Send incentive statements to ${emailPreview.targets.length} recipients?`}
            </p>
            
            {/* Recipients table */}
            <div className="border rounded-lg max-h-36 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-medium">Name</th>
                    <th className="text-left px-3 py-1.5 font-medium">Email</th>
                    <th className="text-right px-3 py-1.5 font-medium">Amount</th>
                    <th className="text-center px-3 py-1.5 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {emailPreview.targets.map(t => (
                    <tr key={t.beneficiary_id} className="border-t">
                      <td className="px-3 py-1.5 font-medium">{t.name}</td>
                      <td className="px-3 py-1.5 text-slate-500">{t.email || '—'}</td>
                      <td className="px-3 py-1.5 text-right">{formatCurrency(t.final_amount)}</td>
                      <td className="px-3 py-1.5 text-center">
                        {t.email_status === 'sent'
                          ? <span className="text-blue-600 font-medium">Resend</span>
                          : <span className="text-slate-400">New</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Email content preview */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-slate-100 px-3 py-2 text-xs font-medium text-slate-600 border-b flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                Email Preview
              </div>
              <div className="bg-white p-0">
                <div style={{ 
                  fontFamily: 'Arial, sans-serif', 
                  maxWidth: '100%',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  color: '#333'
                }}>
                  <div style={{ background: '#1e3a5f', color: 'white', padding: '14px', textAlign: 'center', borderRadius: '6px 6px 0 0' }}>
                    <div style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Incentive Statement</div>
                    <div style={{ marginTop: '4px', opacity: 0.9, fontSize: '13px' }}>
                      {run?.period_start && run?.period_end 
                        ? `${new Date(run.period_start).toLocaleDateString('en-GB', {month:'long', year:'numeric'})}`
                        : 'Current Period'}
                    </div>
                  </div>
                  <div style={{ background: '#f9fafb', padding: '14px', border: '1px solid #e5e7eb', borderTop: 'none' }}>
                    <p>Hi <strong>{emailPreview.targets[0]?.name || 'Recipient'}</strong>,</p>
                    <p style={{ marginTop: '8px' }}>Please find attached your incentive statement for <strong>
                      {run?.period_start && run?.period_end 
                        ? `${new Date(run.period_start).toLocaleDateString('en-GB', {month:'long', year:'numeric'})}`
                        : 'the current period'}
                    </strong>.</p>
                    <div style={{ background: 'white', padding: '12px', borderLeft: '4px solid #c9a227', margin: '12px 0', textAlign: 'center' }}>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>Your incentive earned this period:</div>
                      <div style={{ marginTop: '6px', fontSize: '24px', fontWeight: 'bold', color: '#1e3a5f' }}>
                        {emailPreview.targets.length === 1 
                          ? formatCurrency(emailPreview.targets[0]?.final_amount)
                          : '(Individual amount per recipient)'}
                      </div>
                    </div>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                      The attached PDF contains your full performance breakdown, scoring details, and policy references.
                    </p>
                  </div>
                  <div style={{ textAlign: 'center', padding: '10px', color: '#9ca3af', fontSize: '10px', background: '#f3f4f6', borderRadius: '0 0 6px 6px' }}>
                    <strong>{run?.organisation_name || 'Abbey 5 Star Ltd'}</strong>
                    <div style={{ marginTop: '4px', fontSize: '9px', borderTop: '1px solid #d1d5db', paddingTop: '6px' }}>
                      CONFIDENTIALITY NOTICE: This email and attachments are confidential and intended solely for the named recipient.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-slate-500">
              Each recipient will receive a branded email with their PDF incentive statement attached.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEmailPreview(null)}>Cancel</Button>
              <Button onClick={confirmSendEmails} className="bg-blue-600 hover:bg-blue-700 gap-1.5" data-testid="confirm-send-emails-btn">
                <Send className="h-4 w-4" />
                Send {emailPreview.targets.length === 1 ? 'Email' : `${emailPreview.targets.length} Emails`}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}

// ─────────────────────────────────────────────────────
// Main IncentiveRunPage
// ─────────────────────────────────────────────────────

export default function IncentiveRunPage() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { isAdmin, isSeniorScheduler } = useAuth();
  const { toast } = useToast();

  const canManageIncentives = isAdmin || isSeniorScheduler;

  const [run, setRun] = useState(null);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [typesLoading, setTypesLoading] = useState(true);

  const [form, setForm] = useState({
    title: '', incentive_type_id: '', period_start: '', period_end: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishReadiness, setPublishReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showAddTypeDialog, setShowAddTypeDialog] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDesc, setNewTypeDesc] = useState('');
  const [addingType, setAddingType] = useState(false);

  const isReadOnly = run?.status === 'published';

  const fetchTypes = useCallback(async () => {
    setTypesLoading(true);
    try {
      const res = await api.get('/incentives/types');
      setTypes(res.data);
    } catch { } finally { setTypesLoading(false); }
  }, []);

  const fetchRun = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const res = await api.get(`/incentives/runs/${id}`);
      const data = res.data;
      setRun(data);
      setForm({
        title: data.title || '',
        incentive_type_id: String(data.incentive_type_id),
        period_start: data.period_start,
        period_end: data.period_end,
        notes: data.notes || '',
      });
    } catch (err) {
      const msg = getApiError(err, 'Failed to load incentive run.');
      toast({ title: 'Error', description: msg, variant: 'destructive' });
      navigate('/incentives');
    } finally { setLoading(false); }
  }, [id, isNew, navigate, toast]);

  useEffect(() => { fetchTypes(); fetchRun(); }, [fetchTypes, fetchRun]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validateForm = () => {
    if (!form.incentive_type_id) return 'Please select an incentive type.';
    if (!form.period_start) return 'Please enter a period start date.';
    if (!form.period_end) return 'Please enter a period end date.';
    if (form.period_end < form.period_start) return 'Period end must be on or after period start.';
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) { toast({ title: 'Validation error', description: err, variant: 'destructive' }); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title || null,
        incentive_type_id: Number(form.incentive_type_id),
        period_start: form.period_start,
        period_end: form.period_end,
        notes: form.notes || null,
      };
      if (isNew) {
        const res = await api.post('/incentives/runs', payload);
        toast({ title: 'Created', description: 'Incentive run created as draft.' });
        navigate(`/incentives/${res.data.id}`, { replace: true });
      } else {
        const res = await api.put(`/incentives/runs/${id}`, payload);
        setRun(res.data);
        toast({ title: 'Saved', description: 'Changes saved successfully.' });
      }
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save.'), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handlePublishClick = async () => {
    setReadinessLoading(true);
    setPublishReadiness(null);
    try {
      const res = await api.get(`/incentives/runs/${id}/publish-readiness`);
      setPublishReadiness(res.data);
      setShowPublishDialog(true);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to check readiness.'), variant: 'destructive' });
    } finally {
      setReadinessLoading(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    setShowPublishDialog(false);
    try {
      const res = await api.post(`/incentives/runs/${id}/publish`);
      setRun(res.data);
      toast({ title: 'Published', description: 'Incentive run published and locked.' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to publish.'), variant: 'destructive' });
    } finally { setPublishing(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setShowDeleteDialog(false);
    try {
      await api.delete(`/incentives/runs/${id}`);
      toast({ title: 'Deleted', description: 'Incentive run deleted.' });
      navigate('/incentives');
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to delete.'), variant: 'destructive' });
    } finally { setDeleting(false); }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) return;
    setAddingType(true);
    try {
      const res = await api.post('/incentives/types', { name: newTypeName.trim(), description: newTypeDesc.trim() || null });
      setTypes(prev => [...prev, res.data]);
      setForm(prev => ({ ...prev, incentive_type_id: String(res.data.id) }));
      setShowAddTypeDialog(false);
      setNewTypeName(''); setNewTypeDesc('');
      toast({ title: 'Type created', description: `"${res.data.name}" added.` });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to create incentive type.'), variant: 'destructive' });
    } finally { setAddingType(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const pageTitle = isNew ? 'New Incentive Run' : run?.title || run?.incentive_type_name || `Run #${id}`;
  const incentiveTypeName = run?.incentive_type_name || '';

  return (
    <div className="space-y-6 max-w-6xl" data-testid="incentive-run-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/incentives" className="hover:text-slate-700 flex items-center gap-1">
          <Trophy className="h-4 w-4" />
          Incentives
        </Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-slate-700 font-medium">{pageTitle}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/incentives')} className="shrink-0" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-semibold text-slate-900">{pageTitle}</h1>
              {!isNew && run && <StatusBadge status={run.status} />}
            </div>
            {isReadOnly && (
              <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-1">
                <Lock className="h-3.5 w-3.5" />
                This run is published and locked — no further edits allowed.
              </p>
            )}
          </div>
        </div>

        {!isReadOnly && canManageIncentives && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isNew && isAdmin && (
              <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setShowDeleteDialog(true)} disabled={deleting} data-testid="delete-run-btn">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-run-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {isNew ? 'Create Draft' : 'Save'}
            </Button>
            {!isNew && (
              <Button size="sm" onClick={handlePublishClick} disabled={publishing || readinessLoading}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="publish-run-btn">
                {(publishing || readinessLoading) ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Publish
              </Button>
            )}
          </div>
        )}
        {isReadOnly && !isNew && isAdmin && (
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
            onClick={() => setShowDeleteDialog(true)} disabled={deleting} data-testid="delete-published-run-btn">
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Delete Run
          </Button>
        )}
      </div>

      {/* Run Details form */}
      <Card data-testid="run-form-card">
        <CardHeader>
          <CardTitle className="text-base">Run Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-1.5">
            <Label htmlFor="title">Title <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Input id="title" value={form.title} onChange={(e) => handleChange('title', e.target.value)}
              placeholder="e.g. Q1 2026 Audit Bonus" disabled={isReadOnly} data-testid="run-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Incentive Type <span className="text-red-500">*</span></Label>
            {!typesLoading && types.length === 0 ? (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">No active incentive types configured.</span>{' '}
                  <Link to="/incentives/types" className="underline hover:text-amber-900">Manage incentive types</Link> to create one.
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Select value={form.incentive_type_id} onValueChange={(v) => handleChange('incentive_type_id', v)} disabled={isReadOnly || typesLoading}>
                  <SelectTrigger className="flex-1" data-testid="incentive-type-select">
                    <SelectValue placeholder={typesLoading ? 'Loading types...' : 'Select a type'} />
                  </SelectTrigger>
                  <SelectContent>
                    {types.map(t => <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {!isReadOnly && canManageIncentives && (
                  <Button variant="outline" size="icon" title="Add new incentive type" onClick={() => setShowAddTypeDialog(true)} data-testid="add-type-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="period_start">Period Start <span className="text-red-500">*</span></Label>
              <Input id="period_start" type="date" value={form.period_start}
                onChange={(e) => handleChange('period_start', e.target.value)} disabled={isReadOnly} data-testid="period-start-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="period_end">Period End <span className="text-red-500">*</span></Label>
              <Input id="period_end" type="date" value={form.period_end} min={form.period_start}
                onChange={(e) => handleChange('period_end', e.target.value)} disabled={isReadOnly} data-testid="period-end-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes <span className="text-slate-400 font-normal">(optional)</span></Label>
            <Textarea id="notes" value={form.notes} onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any additional notes about this incentive run…" rows={3} disabled={isReadOnly} data-testid="run-notes-textarea" />
          </div>
        </CardContent>
      </Card>

      {/* Workspace — only for existing non-new runs */}
      {!isNew && run && (
        <WorkspaceSection
          runId={Number(id)}
          runStatus={run.status}
          incentiveTypeName={incentiveTypeName}
        />
      )}

      {/* PDF Statements — only for published runs */}
      {!isNew && run && run.status === 'published' && (
        <>
          <PDFStatementsSection runId={Number(id)} run={run} />
          {/* Export buttons — CSV + Excel (QuickBooks payroll) */}
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={async () => {
                try {
                  const res = await api.get(`/incentives/runs/${id}/export-csv`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `incentive_run_${id}_export.csv`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch {
                  toast({ title: 'Export Failed', description: 'Could not export CSV.', variant: 'destructive' });
                }
              }}
              data-testid="export-csv-btn"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={async () => {
                try {
                  const res = await api.get(`/incentives/runs/${id}/export-xlsx`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', `incentive_run_${id}_payroll.xlsx`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  window.URL.revokeObjectURL(url);
                } catch {
                  toast({ title: 'Export Failed', description: 'Could not export Excel file.', variant: 'destructive' });
                }
              }}
              data-testid="export-xlsx-btn"
              title="Download Excel workbook formatted for QuickBooks payroll import"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              Export Excel
            </Button>
          </div>
        </>
      )}

      {/* Audit trail */}
      {!isNew && run && (
        <Card data-testid="audit-trail-card">
          <CardHeader>
            <CardTitle className="text-base">Audit Trail</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <AuditField label="Created by"
              value={run.created_by_name ? `${run.created_by_name} on ${formatDateTime(run.created_at)}` : null}
              icon={User} />
            {run.updated_by_name && (
              <AuditField label="Last updated by" value={`${run.updated_by_name} on ${formatDateTime(run.updated_at)}`} icon={User} />
            )}
            {run.status === 'published' && run.published_by_name && (
              <AuditField label="Published by" value={`${run.published_by_name} on ${formatDateTime(run.published_at)}`} icon={CheckCircle2} />
            )}
            <AuditField label="Period" value={`${formatDate(run.period_start)} — ${formatDate(run.period_end)}`} icon={Calendar} />
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {/* Publish Readiness Dialog */}
      <AlertDialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Publish Incentive Run?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Once published, this incentive run will be <strong>locked and read-only</strong>. No further edits, re-initializations, or recalculations will be possible.</p>

                {publishReadiness && (
                  <>
                    {/* Blockers */}
                    {publishReadiness.blockers.length > 0 && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1.5">
                        <div className="flex items-center gap-1.5 text-red-700 font-medium text-sm">
                          <AlertCircle className="h-4 w-4" /> Blockers
                        </div>
                        {publishReadiness.blockers.map((b, i) => (
                          <p key={i} className="text-xs text-red-600 ml-5.5">- {b}</p>
                        ))}
                      </div>
                    )}
                    {/* Warnings */}
                    {publishReadiness.warnings.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5">
                        <div className="flex items-center gap-1.5 text-amber-700 font-medium text-sm">
                          <AlertTriangle className="h-4 w-4" /> Warnings
                        </div>
                        {publishReadiness.warnings.map((w, i) => (
                          <p key={i} className="text-xs text-amber-600 ml-5.5">- {w}</p>
                        ))}
                      </div>
                    )}
                    {/* Summary */}
                    {publishReadiness.summary && (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <div className="text-sm font-medium text-slate-700 mb-2">Run Summary</div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          <span className="text-slate-500">Beneficiaries:</span>
                          <span className="font-medium">{publishReadiness.summary.total_beneficiaries}</span>
                          <span className="text-slate-500">Eligible:</span>
                          <span className="font-medium text-emerald-700">{publishReadiness.summary.eligible}</span>
                          <span className="text-slate-500">Excluded:</span>
                          <span className="font-medium">{publishReadiness.summary.excluded}</span>
                          <span className="text-slate-500">Ineligible:</span>
                          <span className="font-medium text-red-600">{publishReadiness.summary.ineligible}</span>
                          <span className="text-slate-500">Overrides applied:</span>
                          <span className="font-medium">{publishReadiness.summary.overrides_applied}</span>
                          <span className="text-slate-500">Total payout:</span>
                          <span className="font-semibold text-indigo-700">{formatCurrency(publishReadiness.summary.total_payout)}</span>
                          {publishReadiness.summary.avg_payout > 0 && (
                            <>
                              <span className="text-slate-500">Avg payout:</span>
                              <span className="font-medium">{formatCurrency(publishReadiness.summary.avg_payout)}</span>
                              <span className="text-slate-500">Range:</span>
                              <span className="font-medium">{formatCurrency(publishReadiness.summary.min_payout)} — {formatCurrency(publishReadiness.summary.max_payout)}</span>
                            </>
                          )}
                        </div>
                        {publishReadiness.completion_pct !== undefined && (
                          <div className="mt-2 flex items-center gap-2">
                            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${publishReadiness.completion_pct >= 100 ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{ width: `${Math.min(100, publishReadiness.completion_pct)}%` }} />
                            </div>
                            <span className="text-xs text-slate-500 font-medium">{publishReadiness.completion_pct}%</span>
                          </div>
                        )}
                      </div>
                    )}
                    {/* Anomalies */}
                    {publishReadiness.anomalies && publishReadiness.anomalies.length > 0 && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1.5">
                        <div className="flex items-center gap-1.5 text-blue-700 font-medium text-sm">
                          <Info className="h-4 w-4" /> Anomalies Detected
                        </div>
                        {publishReadiness.anomalies.map((a, i) => (
                          <p key={i} className="text-xs text-blue-600 ml-5.5">- {a.message}</p>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePublish}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={publishReadiness && !publishReadiness.ready}
              data-testid="publish-confirm-btn"
            >
              {publishReadiness?.ready ? 'Yes, Publish' : 'Cannot Publish'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Incentive Run?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this incentive run and all associated data (beneficiaries, PDFs, email records). This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showAddTypeDialog} onOpenChange={setShowAddTypeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Incentive Type</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input value={newTypeName} onChange={(e) => setNewTypeName(e.target.value)} placeholder="e.g. Audit Bonus" data-testid="new-type-name-input" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label>Description (optional)</Label>
              <Textarea value={newTypeDesc} onChange={(e) => setNewTypeDesc(e.target.value)} rows={2} placeholder="Brief description…" data-testid="new-type-desc-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTypeDialog(false)}>Cancel</Button>
            <Button onClick={handleAddType} disabled={addingType || !newTypeName.trim()} data-testid="add-type-confirm-btn">
              {addingType ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
